import config from "@/v2-backoffice-client/config"

/**
 * Whether this deployment can reach the authoring catalog (v2-features-db,
 * which only exists on the dev SQL server). When false, catalog saves land
 * on the runtime row only — the "hotfix" path the editors warn about — and
 * the authoring-side UI (history, push to production, new entry) is hidden.
 *
 * True for every catalog the backoffice edits (layers, basemaps, nav
 * layers): they all mirror to the same authoring database.
 */
export const IS_CATALOG_AUTHORING_ENABLED = config.envType === "dev"

/**
 * The catalog environment a runtime save mirrors to (matches the backend's
 * AuthoringMirrorEnvironment): the backoffice edits its own runtime, whose
 * seed source is the 'dev' catalog rows; 'prod' rows only change via an
 * explicit "Push to production".
 */
export const AUTHORING_MIRROR_ENVIRONMENT = "dev"

/**
 * Shape of a common.Features code: letters/digits/underscores, letter
 * first. Every create form in the backoffice validates against this, so a
 * code minted here is always addressable by the translation pipeline.
 */
export const FEATURE_CODE_PATTERN = /^[A-Za-z][A-Za-z0-9_]*$/

/**
 * The facets a catalog entry is edited through, one per question an operator is
 * answering: what does it say (text/details), how does it behave
 * (configuration/attributes), who gets it (availability). Every surface that
 * edits an entry — the basemap and nav-layer sheets, the Features board's row —
 * uses these same ids, so the tab in view survives in the URL under one
 * vocabulary rather than three.
 *
 * A surface only renders the facets it has: a generic feature has no
 * thumbnail or position, so its first tab is `text` rather than `details`, and
 * a type value has `attributes` where a feature has `configuration`.
 */
export const FEATURE_TAB = {
  details: "details",
  text: "text",
  configuration: "configuration",
  availability: "availability",
  attributes: "attributes",
} as const

/**
 * Feature types whose Configuration does NOT live on the feature: basemaps
 * keep theirs on gis.Basemaps and map layers on gis.Layers. Editing those
 * through a generic feature editor would write a column nothing reads, so
 * the surfaces that could offer one link to the owning catalog instead
 * (and backoffice.Update_Common_Feature refuses the write outright).
 */
export const FEATURE_TYPE_CODE = {
  uiComponent: 2,
  basemap: 3,
  mapLayer: 4,
} as const

/**
 * Feature types the console reads with an INNER JOIN on
 * common.FeaturesCountries against the viewer's country (api.Get_Gis_Layers,
 * api.Get_Gis_Basemaps): one of these with no country row is hidden in
 * every country. Every other type is read the other way round
 * (api.Get_Common_Features: no row = offered everywhere), which is why the
 * country picker has to say which of the two an empty list means.
 */
export const COUNTRY_GATED_FEATURE_TYPE_CODES: ReadonlySet<number> = new Set([
  FEATURE_TYPE_CODE.basemap,
  FEATURE_TYPE_CODE.mapLayer,
])
